import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Ban,
  Crown,
  KeyRound,
  LogOut,
  Mail,
  MailCheck,
  Pencil,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";
import type { AdminMemberListItem, MemberRole } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsSuperAdmin, useMemberActionMutation, type MemberAction } from "@/features/api";
import { showApiError } from "@/lib/api-client";
import {
  MEMBER_ROLE_LABELS,
  MEMBER_STATUS_LABELS,
  ORGANIZER_STATUS_LABELS,
  dateTime,
  memberStatusVariant,
  organizerStatusVariant,
} from "@/lib/format";

const ASSIGNABLE_ROLES: MemberRole[] = ["Manager", "Editor", "Accountant", "Gate"];

function inviteExpired(member: AdminMemberListItem): boolean {
  return (
    member.status === "Invited" &&
    member.inviteExpiresAt !== null &&
    new Date(member.inviteExpiresAt) < new Date()
  );
}

export function MemberRow({
  member,
  showOrganizer,
  onManage,
}: {
  member: AdminMemberListItem;
  showOrganizer: boolean;
  onManage: () => void;
}) {
  const expired = inviteExpired(member);

  return (
    <li className="flex flex-wrap items-start justify-between gap-4 px-6 py-4">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium break-all">{member.email}</span>
          <Badge variant="outline">{MEMBER_ROLE_LABELS[member.role]}</Badge>
          <Badge variant={memberStatusVariant(member.status)}>
            {expired ? "Davet süresi doldu" : MEMBER_STATUS_LABELS[member.status]}
          </Badge>
          {member.role === "Owner" && !member.emailVerified && (
            <Badge variant="warning">E-posta doğrulanmadı</Badge>
          )}
        </div>
        {showOrganizer && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Link to={`/organizers/${member.organizerId}`} className="font-medium hover:underline">
              {member.organizerName}
            </Link>
            {member.organizerStatus !== "Active" && (
              <Badge variant={organizerStatusVariant(member.organizerStatus)}>
                {ORGANIZER_STATUS_LABELS[member.organizerStatus]}
              </Badge>
            )}
          </div>
        )}
        <div className="text-xs text-muted-foreground">
          {member.status === "Invited" && !expired && member.inviteExpiresAt
            ? `Davet linki ${dateTime(member.inviteExpiresAt)} tarihine kadar geçerli`
            : `Son giriş: ${dateTime(member.lastLoginAt)}`}
          {member.activeSessionCount > 0 && ` · ${member.activeSessionCount} açık oturum`}
        </div>
      </div>
      <Button variant="outline" size="sm" onClick={onManage}>
        Yönet
      </Button>
    </li>
  );
}

type Step =
  | { kind: Exclude<MemberAction["kind"], "email" | "role"> }
  | { kind: "email" }
  | { kind: "role" };

/** Destek: sıfırlama linki, davet, e-posta doğrulama. Diğer işlemler süper admin'e özel (backend de engeller). */
export function MemberManageDialog({
  member,
  onClose,
}: {
  member: AdminMemberListItem | null;
  onClose: () => void;
}) {
  const [step, setStep] = useState<Step | null>(null);

  function close() {
    setStep(null);
    onClose();
  }

  return (
    <Dialog open={member !== null} onOpenChange={(next) => !next && close()} disableBackdropClose={step !== null}>
      <DialogContent>
        {member &&
          (step === null ? (
            <ActionList member={member} onPick={setStep} />
          ) : (
            <ActionConfirm member={member} step={step} onBack={() => setStep(null)} onDone={close} />
          ))}
      </DialogContent>
    </Dialog>
  );
}

function ActionList({ member, onPick }: { member: AdminMemberListItem; onPick: (step: Step) => void }) {
  const isSuper = useIsSuperAdmin();
  const isOwner = member.role === "Owner";
  const loginReady = member.status === "Active" && member.passwordSet;

  const items: { step: Step; icon: ReactNode; label: string; hint: string; danger?: boolean }[] = [];
  if (loginReady) {
    items.push({
      step: { kind: "send-password-reset" },
      icon: <KeyRound className="size-4" />,
      label: "Şifre sıfırlama linki gönder",
      hint: "Kendi e-postasına 1 saat geçerli link gider.",
    });
  }
  if (member.status === "Invited") {
    items.push({
      step: { kind: "resend-invite" },
      icon: <Mail className="size-4" />,
      label: "Daveti yeniden gönder",
      hint: "7 gün geçerli yeni davet linki gider; eskisi çalışmaz.",
    });
  }
  if (isOwner && !member.emailVerified) {
    items.push({
      step: { kind: "verify-email" },
      icon: <MailCheck className="size-4" />,
      label: "E-postayı doğrulanmış say",
      hint: "Kod beklemeden hesap doğrulanır.",
    });
  }
  if (isSuper) {
    if (member.activeSessionCount > 0) {
      items.push({
        step: { kind: "revoke-sessions" },
        icon: <LogOut className="size-4" />,
        label: "Oturumları kapat",
        hint: `${member.activeSessionCount} açık oturum kapanır; hesap pasif olmaz.`,
      });
    }
    items.push({
      step: { kind: "email" },
      icon: <Pencil className="size-4" />,
      label: "E-postayı değiştir",
      hint: isOwner ? "Organizatörün hesap e-postası da değişir." : "Hemen geçerli olur.",
    });
    if (!isOwner) {
      items.push({
        step: { kind: "role" },
        icon: <ShieldCheck className="size-4" />,
        label: "Rolü değiştir",
        hint: `Şu an ${MEMBER_ROLE_LABELS[member.role]}.`,
      });
      if (loginReady) {
        items.push({
          step: { kind: "make-owner" },
          icon: <Crown className="size-4" />,
          label: "Hesap sahibi yap",
          hint: "Şu anki sahip Yönetici olur.",
        });
      }
      items.push(
        member.status === "Disabled"
          ? {
              step: { kind: "enable" },
              icon: <RotateCcw className="size-4" />,
              label: "Aktif yap",
              hint: member.passwordSet ? "Şifresiyle tekrar girebilir." : "Davet bekliyor durumuna döner.",
            }
          : {
              step: { kind: "disable" },
              icon: <Ban className="size-4" />,
              label: "Pasif yap",
              hint: "Panele giremez, oturumları kapanır.",
              danger: true,
            },
      );
    }
  }

  return (
    <>
      <DialogHeader
        title={<span className="break-all">{member.email}</span>}
        description={`${member.organizerName} · ${MEMBER_ROLE_LABELS[member.role]} · ${MEMBER_STATUS_LABELS[member.status]}`}
      />
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Son giriş</dt>
        <dd>{dateTime(member.lastLoginAt)}</dd>
        <dt className="text-muted-foreground">Açık oturum</dt>
        <dd>{member.activeSessionCount}</dd>
        <dt className="text-muted-foreground">Katıldı</dt>
        <dd>{dateTime(member.joinedAt)}</dd>
        {member.status === "Invited" && (
          <>
            <dt className="text-muted-foreground">Davet geçerli</dt>
            <dd>{dateTime(member.inviteExpiresAt)}</dd>
          </>
        )}
        {isOwner && (
          <>
            <dt className="text-muted-foreground">Hesap e-postası</dt>
            <dd>{member.emailVerified ? "Doğrulandı" : "Doğrulanmadı"}</dd>
          </>
        )}
      </dl>

      {items.length > 0 ? (
        <ul className="mt-5 divide-y rounded-md border">
          {items.map((item) => (
            <li key={item.step.kind}>
              <button
                type="button"
                onClick={() => onPick(item.step)}
                className="flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-muted"
              >
                <span className={item.danger ? "mt-0.5 text-destructive" : "mt-0.5 text-muted-foreground"}>
                  {item.icon}
                </span>
                <span className="space-y-0.5">
                  <span className={item.danger ? "block font-medium text-destructive" : "block font-medium"}>
                    {item.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">{item.hint}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">Bu kullanıcı için yapabileceğin işlem yok.</p>
      )}
      {!isSuper && (
        <p className="mt-3 text-xs text-muted-foreground">
          Pasif yapma, oturum kapatma, e-posta / rol değişikliği ve sahiplik devri süper admin'e açık.
        </p>
      )}
    </>
  );
}

function ActionConfirm({
  member,
  step,
  onBack,
  onDone,
}: {
  member: AdminMemberListItem;
  step: Step;
  onBack: () => void;
  onDone: () => void;
}) {
  const mutation = useMemberActionMutation();
  const [email, setEmail] = useState(member.email);
  const [role, setRole] = useState<MemberRole>(
    ASSIGNABLE_ROLES.find((r) => r !== member.role) ?? "Manager",
  );

  const copy = describe(member, step);
  const emailValid = /^\S+@\S+\.\S+$/.test(email.trim()) && email.trim().toLowerCase() !== member.email;
  const canSubmit = step.kind === "email" ? emailValid : step.kind === "role" ? role !== member.role : true;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const action: MemberAction =
      step.kind === "email"
        ? { kind: "email", member, email: email.trim() }
        : step.kind === "role"
          ? { kind: "role", member, role }
          : { kind: step.kind, member };
    try {
      const result = await mutation.mutateAsync(action);
      toast.success(successMessage(action, result));
      onDone();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <form onSubmit={onSubmit}>
      <DialogHeader title={copy.title} description={copy.description} />
      {step.kind === "email" && (
        <div className="space-y-2">
          <Label htmlFor="member-email">Yeni e-posta</Label>
          <Input
            id="member-email"
            type="email"
            autoComplete="off"
            maxLength={320}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
      )}
      {step.kind === "role" && (
        <div className="space-y-2">
          <Label>Yeni rol</Label>
          <Select value={role} onValueChange={(v) => setRole(v as MemberRole)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ASSIGNABLE_ROLES.filter((r) => r !== member.role).map((r) => (
                <SelectItem key={r} value={r}>
                  {MEMBER_ROLE_LABELS[r]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onBack} disabled={mutation.isPending}>
          Geri
        </Button>
        <Button
          type="submit"
          variant={step.kind === "disable" ? "destructive" : "default"}
          disabled={!canSubmit || mutation.isPending}
        >
          {mutation.isPending ? "Kaydediliyor..." : copy.confirm}
        </Button>
      </DialogFooter>
    </form>
  );
}

function describe(
  member: AdminMemberListItem,
  step: Step,
): { title: string; description: string; confirm: string } {
  const isOwner = member.role === "Owner";
  switch (step.kind) {
    case "send-password-reset":
      return {
        title: "Şifre sıfırlama linki gönderilsin mi?",
        description: `${member.email} adresine 1 saat geçerli bir link gider; yeni şifreyi kullanıcı kendisi belirler. Linki kullanana kadar mevcut şifresi çalışır.`,
        confirm: "Linki gönder",
      };
    case "resend-invite":
      return {
        title: "Davet yeniden gönderilsin mi?",
        description: `${member.email} adresine 7 gün geçerli yeni bir davet linki gider. Önceki link çalışmaz.`,
        confirm: "Daveti gönder",
      };
    case "verify-email":
      return {
        title: "Hesap e-postası doğrulanmış sayılsın mı?",
        description: `${member.organizerName} hesabı, ${member.email} adresine gelen kod girilmeden doğrulanmış olur. Bekleyen doğrulama kodları geçersiz olur.`,
        confirm: "Doğrulanmış say",
      };
    case "disable":
      return {
        title: `${member.email} pasif yapılsın mı?`,
        description:
          "Panele giremez, açık oturumları hemen kapanır. Kayıtları silinmez; tekrar aktif yapılabilir. Kullanıcıya e-posta gitmez.",
        confirm: "Pasif yap",
      };
    case "enable":
      return {
        title: `${member.email} tekrar aktif olsun mu?`,
        description: member.passwordSet
          ? "Şifresiyle tekrar giriş yapabilir."
          : "Daveti hiç kabul etmemişti; davet bekliyor durumuna döner. Ardından daveti yeniden gönder.",
        confirm: "Aktif yap",
      };
    case "revoke-sessions":
      return {
        title: "Oturumları kapatılsın mı?",
        description: `${member.activeSessionCount} açık oturum kapanır; tekrar giriş yapması gerekir. Hesap pasif olmaz, destek oturumları etkilenmez.`,
        confirm: "Oturumları kapat",
      };
    case "email":
      return {
        title: "E-postayı değiştir",
        description: [
          "Hemen geçerli olur, kimseye e-posta gitmez. Bekleyen şifre sıfırlama linki çalışmaz.",
          isOwner ? `Hesap sahibi olduğu için ${member.organizerName} hesabının e-postası da değişir.` : null,
          member.status === "Invited" ? "Davet linki de çalışmaz; değiştirdikten sonra daveti yeniden gönder." : null,
        ]
          .filter(Boolean)
          .join(" "),
        confirm: "E-postayı değiştir",
      };
    case "role":
      return {
        title: "Rolü değiştir",
        description: `${member.email} şu an ${MEMBER_ROLE_LABELS[member.role]}. Yeni rol açık oturumlarında hemen geçerli olur.`,
        confirm: "Rolü değiştir",
      };
    case "make-owner":
      return {
        title: `${member.email} hesap sahibi olsun mu?`,
        description: `Şu anki sahip Yönetici olur. ${member.organizerName} hesabının e-postası ${member.email} olur. Kimseye e-posta gitmez.`,
        confirm: "Hesap sahibi yap",
      };
  }
}

function successMessage(action: MemberAction, result: AdminMemberListItem): string {
  switch (action.kind) {
    case "send-password-reset":
      return `Şifre sıfırlama linki gönderildi: ${result.email}`;
    case "resend-invite":
      return `Davet yeniden gönderildi: ${result.email}`;
    case "verify-email":
      return "Hesap e-postası doğrulandı.";
    case "disable":
      return `${result.email} pasif yapıldı.`;
    case "enable":
      return result.status === "Invited"
        ? `${result.email} davet bekliyor durumunda; daveti yeniden gönder.`
        : `${result.email} tekrar aktif.`;
    case "revoke-sessions":
      return "Oturumlar kapatıldı.";
    case "email":
      return `E-posta değişti: ${result.email}`;
    case "role":
      return `${result.email} artık ${MEMBER_ROLE_LABELS[result.role]}.`;
    case "make-owner":
      return `${result.email} artık hesap sahibi.`;
  }
}
